import { formatDate, formatDateTime } from '@/utils/datetime';
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClockClockwise,
  ArrowsClockwise,
  Trash,
  CheckCircle,
  WarningCircle,
  PaperPlaneTilt,
  CalendarBlank,
  Eye,
  Copy,
  Printer,
  PencilSimple,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/ui/DataTable';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  submission_notification: '申請通知',
  pre_2d: '不在連絡 (事前)',
  on_start: '不在連絡 (当日)',
  manual: '手動再送',
  test: 'テスト送信',
  scheduled: '不在連絡',
};

const FORM_LABEL = {
  overtime: '勤務指示書',
  leave: '休暇届',
  leave_transfer: '振替休暇申請書',
  work_declaration: '勤務時間申告書',
  travel_expense: '出張旅費精算請求書',
};

const SCHED_STATUS_LABEL = {
  pending: '送信予定',
  sent: '送信済み',
  skipped: '対象外',
  error: '失敗',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return formatDateTime(iso);
  } catch (_e) {
    return iso;
  }
}

function fmtDateOnly(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return formatDate(iso);
  } catch (_e) {
    return iso;
  }
}

export default function EmailLog() {
  const [logs, setLogs] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [running, setRunning] = useState(false);
  const [activeLog, setActiveLog] = useState(null);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [schedulePreview, setSchedulePreview] = useState(null);
  const [schedulePreviewing, setSchedulePreviewing] = useState(false);
  const iframeRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [logRes, schedRes] = await Promise.all([
        axios.get(`${API}/email-log`, { params: { limit: 500 } }),
        axios.get(`${API}/scheduled-emails`, { params: { status: 'pending' } }),
      ]);
      setLogs(logRes.data || []);
      setScheduled(schedRes.data || []);
    } catch (e) {
      toast.error('メール送信履歴の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return logs.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (kindFilter !== 'all' && row.kind !== kindFilter) return false;
      return true;
    });
  }, [logs, statusFilter, kindFilter]);

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/scheduled-emails/run-now`);
      toast.success('送信予定のメールを処理しました');
      await load();
    } catch (e) {
      toast.error('処理に失敗しました');
    } finally {
      setRunning(false);
    }
  };

  const openSchedulePreview = async (sched) => {
    setActiveSchedule(sched);
    setSchedulePreview(null);
    setSchedulePreviewing(true);
    try {
      const res = await axios.get(`${API}/scheduled-emails/${encodeURIComponent(sched.id)}/preview`);
      setSchedulePreview(res.data);
    } catch (_e) {
      toast.error('プレビューの取得に失敗しました');
      setActiveSchedule(null);
    } finally {
      setSchedulePreviewing(false);
    }
  };

  const clearLog = async () => {
    if (!window.confirm('メール送信履歴をすべて削除しますか？この操作は取り消せません。')) return;
    try {
      await axios.delete(`${API}/email-log`);
      toast.success('履歴を削除しました');
      await load();
    } catch (e) {
      toast.error('削除に失敗しました');
    }
  };

  const kindOptions = useMemo(() => {
    const seen = new Set(logs.map((l) => l.kind).filter(Boolean));
    return Array.from(seen);
  }, [logs]);

  return (
    <div className="space-y-8" data-testid="email-log-page">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-400">設定</p>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mt-1">メール送信履歴</h1>
          <p className="text-sm text-zinc-500 mt-1">休暇届の不在連絡・申請通知・テスト送信などの履歴を確認できます。</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="email-log-reload">
            <ArrowsClockwise size={16} className="mr-1" /> 再読み込み
          </Button>
          <Button variant="outline" size="sm" onClick={runNow} disabled={running} data-testid="email-log-run-now">
            <PaperPlaneTilt size={16} className="mr-1" /> 送信予定を今すぐ実行
          </Button>
          <Button variant="outline" size="sm" onClick={clearLog} data-testid="email-log-clear" className="text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash size={16} className="mr-1" /> 履歴を消去
          </Button>
        </div>
      </header>

      {/* Pending schedule */}
      <section className="space-y-2" data-testid="email-log-schedule">
        <div className="flex items-center gap-2 px-1">
          <CalendarBlank size={18} className="text-teal-700" weight="duotone" />
          <h2 className="text-sm font-semibold text-zinc-900">送信予定（休暇届の不在連絡）</h2>
          <span className="ml-auto text-xs text-zinc-500">毎朝 7:00 (JST) に自動送信</span>
        </div>
        <DataTable
          rows={scheduled}
          rowKey={(s) => s.id}
          rowTestId={(s) => `schedule-row-${s.id}`}
          onRowClick={(s) => openSchedulePreview(s)}
          empty={{ icon: <CalendarBlank size={36} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: '現在、送信予定のメールはありません。' }}
          columns={[
            { key: 'send_at', header: '送信予定日時', cell: (s) => <span className="text-zinc-900">{fmtDate(s.send_at || s.send_on)}</span> },
            { key: 'kind', primary: true, header: '種別', cell: (s) => <span className="inline-flex items-center gap-1.5"><Eye size={14} className="text-zinc-400" />{KIND_LABEL[s.kind] || s.kind}</span> },
            { key: 'submission_id', header: '提出ID', cell: (s) => (
              <span onClick={(e) => e.stopPropagation()}>
                <Link to={`/submission/${s.submission_id}`} className="text-teal-700 hover:underline text-xs font-mono">{s.submission_id}</Link>
              </span>
            ) },
            { key: 'status', header: '', cell: (s) => (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                <ClockClockwise size={12} /> {SCHED_STATUS_LABEL[s.status] || s.status}
              </span>
            ) },
          ]}
        />
      </section>

      {/* Log filters */}
      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-3 px-1">
          <h2 className="text-sm font-semibold text-zinc-900">送信履歴</h2>
          <div className="ml-auto flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] h-9" data-testid="email-log-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全ステータス</SelectItem>
                <SelectItem value="sent">送信成功</SelectItem>
                <SelectItem value="error">送信失敗</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-[180px] h-9" data-testid="email-log-filter-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全種別</SelectItem>
                {kindOptions.map((k) => (
                  <SelectItem key={k} value={k}>{KIND_LABEL[k] || k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-zinc-200 px-5 py-10 text-center text-sm text-zinc-500">読み込み中…</div>
        ) : (
          <DataTable
            dataTestId="email-log-table"
            rows={filtered}
            rowKey={(row) => row.id}
            rowTestId={(row) => `email-log-row-${row.id}`}
            onRowClick={(row) => setActiveLog(row)}
            empty={{ icon: <PaperPlaneTilt size={36} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: logs.length === 0 ? 'まだメールは送信されていません。' : '条件に一致する履歴はありません。' }}
            columns={[
              { key: 'sent_at', header: '送信日時', cell: (row) => <span className="text-zinc-900 whitespace-nowrap">{fmtDate(row.sent_at)}</span> },
              { key: 'kind', header: '種別', cell: (row) => <span className="text-zinc-700 whitespace-nowrap">{KIND_LABEL[row.kind] || row.kind}</span> },
              { key: 'form_type', header: 'フォーム', cell: (row) => <span className="text-zinc-700 whitespace-nowrap">{FORM_LABEL[row.form_type] || row.form_type || '—'}</span> },
              { key: 'subject', primary: true, header: '件名', cell: (row) => (
                <span className="inline-flex items-center gap-1.5" title={row.subject}><Eye size={14} className="text-zinc-400 shrink-0" />{row.subject}</span>
              ) },
              { key: 'recipients', header: '宛先', cell: (row) => (
                <div className="max-w-[280px] truncate" title={(row.recipients || []).join(', ')}>{(row.recipients || []).join(', ') || '—'}</div>
              ) },
              { key: 'submission_id', header: '提出ID', cell: (row) => (
                row.submission_id ? (
                  <span onClick={(e) => e.stopPropagation()}>
                    <Link to={`/submission/${row.submission_id}`} className="text-teal-700 hover:underline text-xs font-mono">{row.submission_id}</Link>
                  </span>
                ) : <span className="text-zinc-400">—</span>
              ) },
              { key: 'status', header: '', cell: (row) => (
                row.status === 'sent' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <CheckCircle size={12} weight="fill" /> 送信成功
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200" title={row.error || ''}>
                    <WarningCircle size={12} weight="fill" /> 送信失敗
                  </span>
                )
              ) },
            ]}
          />
        )}
      </section>

      {/* Helper note */}
      <div className="text-xs text-zinc-500 flex items-center gap-2">
        <ClockClockwise size={14} />
        本日 {fmtDateOnly(new Date().toISOString())} に表示されている送信予定は、日本時間 (JST) で 7:00 に自動配信されます。
      </div>

      {/* Scheduled-email preview modal */}
      <Dialog open={!!activeSchedule} onOpenChange={(open) => { if (!open) { setActiveSchedule(null); setSchedulePreview(null); } }}>
        <DialogContent
          className="max-w-3xl p-0 gap-0 overflow-hidden"
          data-testid="schedule-preview-modal"
        >
          <DialogHeader className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/60">
            <DialogTitle className="text-base font-semibold text-zinc-900">
              送信予定のプレビュー
            </DialogTitle>
            <p className="text-xs text-zinc-500 mt-1">
              指定の日時に下記の内容で自動送信されます。
            </p>
          </DialogHeader>

          {schedulePreviewing || !schedulePreview ? (
            <div className="h-[60vh] flex items-center justify-center text-sm text-zinc-500">
              <ArrowsClockwise size={20} className="animate-spin mr-2" /> 読み込み中…
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/60 space-y-2 text-xs">
                <div className="grid grid-cols-[88px_1fr] gap-x-4 items-baseline">
                  <span className="text-zinc-400">送信予定</span>
                  <span className="text-zinc-800 tabular-nums">{fmtDate(schedulePreview.send_at)}</span>
                </div>
                <div className="grid grid-cols-[88px_1fr] gap-x-4 items-baseline">
                  <span className="text-zinc-400">種別</span>
                  <span className="text-zinc-800">{KIND_LABEL[schedulePreview.kind] || schedulePreview.kind}</span>
                </div>
                <div className="grid grid-cols-[88px_1fr] gap-x-4 items-baseline">
                  <span className="text-zinc-400">宛先</span>
                  <span className="text-zinc-800 break-all">{(schedulePreview.recipients || []).join(', ') || '—'}</span>
                </div>
                <div className="grid grid-cols-[88px_1fr] gap-x-4 items-baseline pt-2 border-t border-zinc-200/70">
                  <span className="text-zinc-400">件名</span>
                  <span className="text-zinc-900 text-sm font-semibold break-all leading-snug">{schedulePreview.subject}</span>
                </div>
              </div>
              {schedulePreview.body_html ? (
                <iframe
                  title="schedule-preview-body"
                  srcDoc={schedulePreview.body_html}
                  sandbox=""
                  className="w-full h-[55vh] border-0 bg-white"
                  data-testid="schedule-preview-iframe"
                />
              ) : (
                <div className="h-[55vh] flex items-center justify-center text-sm text-zinc-500">
                  本文がありません
                </div>
              )}
            </>
          )}

          <div className="px-6 py-3 border-t border-zinc-200 bg-zinc-50/60 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="schedule-preview-edit-template"
              onClick={() => { setActiveSchedule(null); setSchedulePreview(null); navigate('/settings/email-templates'); }}
            >
              <PencilSimple size={14} className="mr-1" /> テンプレートを編集
            </Button>
            <Button
              size="sm"
              onClick={() => { setActiveSchedule(null); setSchedulePreview(null); }}
              data-testid="schedule-preview-close"
            >
              閉じる
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email body preview modal */}
      <Dialog open={!!activeLog} onOpenChange={(open) => { if (!open) setActiveLog(null); }}>
        <DialogContent
          className="max-w-3xl p-0 gap-0 overflow-hidden"
          data-testid="email-log-detail"
        >
          {activeLog && (
            <>
              <DialogHeader className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/60">
                <DialogTitle className="text-base font-semibold text-zinc-900 truncate">
                  {activeLog.subject}
                </DialogTitle>
                <div className="mt-3 space-y-1.5 text-xs">
                  <div className="grid grid-cols-[88px_1fr] gap-x-4 items-baseline">
                    <span className="text-zinc-400">送信日時</span>
                    <span className="text-zinc-700 tabular-nums">{fmtDate(activeLog.sent_at)}</span>
                  </div>
                  <div className="grid grid-cols-[88px_1fr] gap-x-4 items-baseline">
                    <span className="text-zinc-400">種別</span>
                    <span className="text-zinc-700">{KIND_LABEL[activeLog.kind] || activeLog.kind}</span>
                  </div>
                  <div className="grid grid-cols-[88px_1fr] gap-x-4 items-baseline">
                    <span className="text-zinc-400">宛先</span>
                    <span className="text-zinc-700 break-all">{(activeLog.recipients || []).join(', ') || '—'}</span>
                  </div>
                  <div className="grid grid-cols-[88px_1fr] gap-x-4 items-baseline">
                    <span className="text-zinc-400">テンプレ</span>
                    <span className="text-zinc-700 font-mono">{activeLog.template || '—'}</span>
                  </div>
                  <div className="grid grid-cols-[88px_1fr] gap-x-4 items-baseline">
                    <span className="text-zinc-400">ステータス</span>
                    <span>
                      {activeLog.status === 'sent' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle size={12} weight="fill" /> 送信成功
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200">
                          <WarningCircle size={12} weight="fill" /> 送信失敗
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                {activeLog.status === 'error' && activeLog.error && (
                  <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 whitespace-pre-wrap font-mono">
                    {activeLog.error}
                  </p>
                )}
              </DialogHeader>

              <div className="bg-white">
                {activeLog.body_html ? (
                  <iframe
                    ref={iframeRef}
                    title="email-body"
                    srcDoc={activeLog.body_html}
                    sandbox=""
                    className="w-full h-[60vh] border-0 bg-white"
                    data-testid="email-log-iframe"
                  />
                ) : activeLog.body_text ? (
                  <pre
                    className="w-full h-[60vh] overflow-auto px-6 py-5 text-sm text-zinc-800 whitespace-pre-wrap font-sans m-0"
                    data-testid="email-log-text"
                  >
                    {activeLog.body_text}
                  </pre>
                ) : (
                  <div className="h-[60vh] flex items-center justify-center text-sm text-zinc-500">
                    この履歴には本文が保存されていません。
                  </div>
                )}
              </div>

              <div className="px-6 py-3 border-t border-zinc-200 bg-zinc-50/60 flex items-center justify-end gap-2">
                {(activeLog.body_html || activeLog.body_text) && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="email-log-copy"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(activeLog.body_text || activeLog.body_html || '');
                          toast.success('本文をコピーしました');
                        } catch (_e) {
                          toast.error('コピーに失敗しました');
                        }
                      }}
                    >
                      <Copy size={14} className="mr-1" /> コピー
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="email-log-print"
                      onClick={() => {
                        try {
                          if (activeLog.body_html && iframeRef.current && iframeRef.current.contentWindow) {
                            iframeRef.current.contentWindow.focus();
                            iframeRef.current.contentWindow.print();
                          } else {
                            window.print();
                          }
                        } catch (_e) {
                          toast.error('印刷に失敗しました');
                        }
                      }}
                    >
                      <Printer size={14} className="mr-1" /> 印刷
                    </Button>
                  </>
                )}
                <Button size="sm" onClick={() => setActiveLog(null)} data-testid="email-log-close">
                  閉じる
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
